import { FeaturedMedia as FeaturedMediaType } from 'types'
import { Text } from 'blaze-ui/Text/Text'
import { Field } from 'components/Wizards/Field/Field'
import { CheckboxGroup } from 'components/CheckboxGroup/CheckboxGroup'
import { useCheckboxGroupContext } from 'components/CheckboxGroup/CheckboxGroupContext'
import { useCheckboxGroupItem } from '@react-aria/checkbox'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import { Check02 } from 'blaze-ui/icons/16'
import { Play3 } from 'blaze-ui/icons'
import { Credits } from 'blaze-ui/icons/20/Credits'
import { useRef, ReactNode, useState, useCallback } from 'react'
import cx from 'classnames'
import Styles from '../ContentPreferencesScreen.module.scss'
import { SafeNumberInput } from 'almanac-ui/TextInputField/SafeNumberInput'

export type MediaWeights = {
  video: number
  image: number
  text: number
}

export type CreditCosts = {
  video: string
  image: string
  text: string
}

type Props = {
  featuredMedia: FeaturedMediaType[]
  weights: MediaWeights
  onUpdate: (featuredMedia: string[], weights: MediaWeights) => void
  hideLabel?: boolean
  creditCosts?: CreditCosts
  isMobile?: boolean
}

export type SelectableCardProps = {
  value: string
  label: string
  weight?: number
  onWeightChange?: (value: number) => void
  children?: ReactNode
  isDisabled?: boolean
  creditCost?: string
}

export const SelectableCard = ({
  value,
  label,
  weight,
  onWeightChange,
  children,
  isDisabled = false,
  creditCost,
}: SelectableCardProps) => {
  const state = useCheckboxGroupContext()
  const ref = useRef<HTMLInputElement>(null)
  const { inputProps } = useCheckboxGroupItem({ value, isDisabled }, state, ref)
  const isSelected = state.isSelected(value)
  const isWeightInputVisible = weight !== undefined && onWeightChange

  return (
    <div className={Styles.contentTypeCardWrapper}>
      <label
        className={cx(Styles.contentTypeCard, {
          [Styles.contentTypeCardDisabled]: isDisabled,
        })}
      >
        <VisuallyHidden>
          <input {...inputProps} ref={ref} />
        </VisuallyHidden>
        <div className={cx(Styles.contentTypeCheckbox, { [Styles.contentTypeCheckboxSelected]: isSelected })}>
          {isSelected && <Check02 size={12} color="var(--light-100)" />}
        </div>
        <div className={Styles.contentTypePreview}>{children}</div>
        <div className={Styles.contentTypeLabel}>
          <Text variant="smallList" color="var(--dark-80)">
            {label}
          </Text>
        </div>
        {isWeightInputVisible && (
          <div
            className={Styles.contentTypeWeightInput}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <div className={Styles.weightInputWrapper}>
              <SafeNumberInput
                name={`${value}-weight`}
                className={Styles.weightInput}
                size="sm"
                value={weight}
                onChange={onWeightChange}
                minValue={0}
                step={1}
                disabled={!isSelected}
                aria-label={`${label} percentage`}
              />
            </div>
            <Text variant="metadata" color="var(--dark-40)">
              of total content
            </Text>
          </div>
        )}
      </label>
      {creditCost && (
        <div className={Styles.contentTypeCreditCost}>
          <Credits size={14} color="var(--dark-40)" />
          <Text variant="metadata" color="var(--dark-40)">
            {creditCost}
          </Text>
        </div>
      )}
    </div>
  )
}

export const MobileSelectableRow = ({
  value,
  label,
  weight,
  onWeightChange,
  children,
  isDisabled = false,
}: SelectableCardProps) => {
  const state = useCheckboxGroupContext()
  const ref = useRef<HTMLInputElement>(null)
  const { inputProps } = useCheckboxGroupItem({ value, isDisabled }, state, ref)
  const isSelected = state.isSelected(value)
  const isWeightInputVisible = weight !== undefined && onWeightChange

  return (
    <label className={Styles.mobileContentTypeRow}>
      <VisuallyHidden>
        <input {...inputProps} ref={ref} />
      </VisuallyHidden>
      <div className={cx(Styles.mobileContentTypeCheckbox, { [Styles.mobileContentTypeCheckboxSelected]: isSelected })}>
        {isSelected && <Check02 size={12} color="var(--light-100)" />}
      </div>
      <div className={Styles.mobileContentTypeThumbnail}>{children}</div>
      <Text variant="smallList" color="var(--dark-80)">
        {label}
      </Text>
      {isWeightInputVisible && (
        <div
          className={Styles.mobileContentTypeWeight}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <div className={Styles.mobileWeightInputWrapper}>
            <SafeNumberInput
              name={`${value}-weight`}
              className={Styles.mobileWeightInput}
              size="sm"
              value={weight}
              onChange={onWeightChange}
              minValue={0}
              step={1}
              disabled={!isSelected}
              aria-label={`${label} percentage`}
            />
          </div>
          <Text variant="metadata" color="var(--dark-40)">
            of total content
          </Text>
        </div>
      )}
    </label>
  )
}

const redistributeWeights = (enabledTypes: FeaturedMediaType[]): MediaWeights => {
  if (enabledTypes.length === 0) {
    return { video: 0, image: 0, text: 0 }
  }

  const equalWeight = Math.floor(100 / enabledTypes.length)

  return {
    video: enabledTypes.includes('video') ? equalWeight : 0,
    image: enabledTypes.includes('image') ? equalWeight : 0,
    text: enabledTypes.includes('text') ? equalWeight : 0,
  }
}

const getEnabledWeightsSum = (weights: MediaWeights, enabledTypes: FeaturedMediaType[]): number => {
  return enabledTypes.reduce((sum, type) => sum + weights[type], 0)
}

export const ContentTypesSelector = (props: Props) => {
  const { featuredMedia, weights, onUpdate, hideLabel = false, creditCosts, isMobile = false } = props
  const [localWeights, setLocalWeights] = useState<MediaWeights>(weights)

  const enabledWeightsSum = getEnabledWeightsSum(localWeights, featuredMedia)
  const isValid = enabledWeightsSum >= 99 && enabledWeightsSum <= 100

  const handleWeightChange = useCallback(
    (type: FeaturedMediaType, value: number) => {
      const newWeights = { ...localWeights, [type]: value }
      setLocalWeights(newWeights)
      onUpdate(featuredMedia, newWeights)
    },
    [localWeights, featuredMedia, onUpdate],
  )

  const handleFeaturedMediaChange = useCallback(
    (value: string[]) => {
      if (!value.length) return

      const newEnabledTypes = value as FeaturedMediaType[]
      const newWeights = redistributeWeights(newEnabledTypes)

      setLocalWeights(newWeights)
      onUpdate(value, newWeights)
    },
    [onUpdate],
  )

  const mobileContent = (
    <>
      <CheckboxGroup
        value={featuredMedia}
        className={Styles.mobileContentTypesList}
        aria-label="Content Types and Ratio"
        onChange={handleFeaturedMediaChange}
      >
        <MobileSelectableRow
          value="video"
          label="Video"
          weight={localWeights.video}
          onWeightChange={(value) => handleWeightChange('video', value)}
        >
          <div className={Styles.previewContainer}>
            <img
              src="https://res.cloudinary.com/almanac/image/upload/v1767973038/blaze_assets/content_preferences/image_ewyssy_wqi02k.webp"
              alt="Video preview"
              className={Styles.previewImage}
            />
            <div className={Styles.videoPlayButton}>
              <Play3 size={10} />
            </div>
          </div>
        </MobileSelectableRow>
        <MobileSelectableRow
          value="image"
          label="Image"
          weight={localWeights.image}
          onWeightChange={(value) => handleWeightChange('image', value)}
        >
          <img
            src="https://res.cloudinary.com/almanac/image/upload/v1767973106/blaze_assets/content_preferences/60103e9257513a7aac77cd0d152d16b36a6fd825_lvpz0t_yd6nmk.webp"
            alt="preview"
            className={Styles.previewImage}
          />
        </MobileSelectableRow>
        <MobileSelectableRow
          value="text"
          label="Text"
          weight={localWeights.text}
          onWeightChange={(value) => handleWeightChange('text', value)}
        >
          <div className={Styles.textPreviewCard}>
            <span className={Styles.mobileTextPreviewContent}>Get access to discounts and savings</span>
          </div>
        </MobileSelectableRow>
      </CheckboxGroup>
      {!isValid && featuredMedia.length > 0 && (
        <div className={Styles.weightValidationError}>
          <Text variant="metadata" color="var(--red-50)">
            Percentages must add up to 100% (currently {enabledWeightsSum}%)
          </Text>
        </div>
      )}
    </>
  )

  const desktopContent = (
    <>
      <CheckboxGroup
        value={featuredMedia}
        className={Styles.contentTypesCardGroup}
        aria-label="Content Types and Ratio"
        onChange={handleFeaturedMediaChange}
      >
        <SelectableCard
          value="video"
          label="Videos"
          weight={localWeights.video}
          onWeightChange={(value) => handleWeightChange('video', value)}
          creditCost={creditCosts?.video}
        >
          <div className={Styles.previewContainer}>
            <img
              src="https://res.cloudinary.com/almanac/image/upload/v1767973038/blaze_assets/content_preferences/image_ewyssy_wqi02k.webp"
              alt="Video preview"
              className={Styles.previewImage}
            />
            <div className={Styles.videoPlayButton}>
              <Play3 size={14} />
            </div>
          </div>
        </SelectableCard>
        <SelectableCard
          value="image"
          label="Images"
          weight={localWeights.image}
          onWeightChange={(value) => handleWeightChange('image', value)}
          creditCost={creditCosts?.image}
        >
          <img
            src="https://res.cloudinary.com/almanac/image/upload/v1767973106/blaze_assets/content_preferences/60103e9257513a7aac77cd0d152d16b36a6fd825_lvpz0t_yd6nmk.webp"
            alt="preview"
            className={Styles.previewImage}
          />
        </SelectableCard>
        <SelectableCard
          value="text"
          label="Text Only"
          weight={localWeights.text}
          onWeightChange={(value) => handleWeightChange('text', value)}
          creditCost={creditCosts?.text}
        >
          <div className={Styles.textPreviewCard}>
            <span className={Styles.textPreviewContent}>Get access to discounts and savings</span>
          </div>
        </SelectableCard>
      </CheckboxGroup>
      {!isValid && featuredMedia.length > 0 && (
        <div className={Styles.weightValidationError}>
          <Text variant="metadata" color="var(--red-50)">
            Percentages must add up to 100% (currently {enabledWeightsSum}%)
          </Text>
        </div>
      )}
    </>
  )

  const content = isMobile ? mobileContent : desktopContent

  if (hideLabel) {
    return <div data-testid="content-types-selector">{content}</div>
  }

  return (
    <Field label="Content Types and Ratio" headingLevel={3} data-testid="content-types-selector">
      {content}
    </Field>
  )
}
